import { Link, Navigate, Outlet, useLocation } from 'react-router';
import { buttonClasses } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { guestSessionStore } from '@/auth/guest-session';
import { staffSessionStore } from '@/auth/staff-session';
import { useGuestSession, useStaffSession } from '@/auth/react';
import { CantScanCard } from '@/features/customer/CantScan';

/**
 * Shown on guest pages when there is no table session. `lastQrToken` (known only while the page stays
 * open) gives a customer whose session just ended a one-tap way back; otherwise they scan again.
 * Only the guest session is involved: staff sign-in is untouched.
 */
export function GuestSessionMissing({ lastQrToken }: { lastQrToken?: string | null }) {
  const reason = guestSessionStore.endReason();
  const ended = reason === 'expired' || reason === 'unauthorized';
  if (ended) {
    return (
      <EmptyState
        icon="alert"
        title="Your session has ended"
        message="Scan the QR code again to pick up where you left off."
        action={
          lastQrToken ? (
            <Link
              to={`/q/${encodeURIComponent(lastQrToken)}`}
              className={buttonClasses({ variant: 'primary' })}
            >
              Start again
            </Link>
          ) : undefined
        }
      />
    );
  }
  return (
    <EmptyState
      icon="pin"
      title="Scan to start ordering"
      message="The QR code on your table or seat opens the menu for your visit."
      action={<CantScanCard className="mt-2" />}
    />
  );
}

/** Guest pages need a table session. Without one the guest is asked to scan the QR code again. */
export function RequireGuestSession() {
  const session = useGuestSession();
  if (session) return <Outlet />;
  return <GuestSessionMissing />;
}

/** Staff pages need a staff session; otherwise go to sign-in and come back afterwards. */
export function RequireStaffSession() {
  const session = useStaffSession();
  const location = useLocation();
  if (session) return <Outlet />;
  return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
}

export function staffEndNotice(): string | null {
  const reason = staffSessionStore.endReason();
  if (reason === 'expired' || reason === 'unauthorized')
    return 'Your session has ended. Please sign in again.';
  return null;
}
